/* @flow */
import React, { useState } from "react";
import styled, { css } from "styled-components";

import Gettext, { withGettext } from "../../common/Gettext";
import { withFetch, get } from "../../common/fetch";

import ErrorMessage from "../ErrorMessage";
import BtnFormSubmit from "../BtnFormSubmit";

import FeatureFlags from "../../../FeatureFlags";
import { FieldContainer } from "../../ReVerification/style";
import { InfoIcon } from "../../ReVerification/Icons";
import Container from "../StyledComponents/Container";
import Link from "../StyledComponents/FakeLink";
import { Link as _Link } from "../../common/Link";
import validators from "../validators";
import Input from "../Input";
import { LOGIN_TAB_ID } from "../Auth";
import PhoneInput, {
  PhoneInputHint,
  ripOffLeadingZero,
} from "../../ReVerification/Phone/PhoneInput";

import { fontSize } from "../../common/styled-mixins";

const Text = styled.p`
  direction: ${(props) => props.theme.direction};
  padding: 20px 20px 9px 20px;
  margin: 0;
  font-size: 21px;
  font-weight: 300;
  line-height: 34px;
  color: #fff;

  @media (max-width: 360px) {
    font-size: 20px;
    line-height: 28px;
  }
`;

const SubmitContainer = styled.div`
  padding: 11px 0;
  ${"" /* height: 68px; */} position: relative;
  text-align: center;
  vertical-align: middle;
`;

const BackContainer = styled.div`
  margin-top: 10px;
  font-size: ${fontSize(15)};
  color: #fff;
  line-height: 1;
  ${({ theme, isDarkPage }) =>
    theme.newIndex &&
    css`
      font-size: ${fontSize(18)};
      text-align: center;
      line-height: 1.2;
      font-weight: bold;
      color: ${isDarkPage ? "#ffffff" : "rgba(34, 34, 34, 0.59)"};
    `};
`;

const BackToLogin = styled(Link)`
  text-decoration: none !important;
  color: ${({ theme }) => theme.accent} !important;
  transition: all 0.2s ease;
  font-size: ${fontSize(18)};
  line-height: 1;

  :hover,
  :active,
  :focus {
    color: #ffc54c !important;
  }
  cursor: pointer;

  ${({ theme }) =>
    theme.newIndex &&
    css`
      color: ${theme.accent} !important;
      &:hover {
        color: #ffffff !important;
      }
    `};
`;

const SuppurLinkContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const SupportLink = styled(_Link)`
  display: block;
  margin: 0 auto;
  padding: 0;
  font-size: ${fontSize(14)};
  line-height: 1.2;
  text-decoration: none;
  color: ${({ theme }) => theme.accent};
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ theme }) =>
    theme.newIndex &&
    css`
      font-size: ${fontSize(18)};
      text-decoration: underline;
    `};

  :hover,
  :active,
  :focus {
    color: #ffc54c;
    text-decoration: none;
  }
`;

const PhoneFieldContainer = styled(FieldContainer)`
  position: relative;
  width: 100%;

  @media (max-width: 959px) {
    display: flex;
  }
`;

const InputContainer = styled.div`
  padding: 4px 0;
  position: relative;

  ${({ theme }) =>
    theme.newIndex &&
    css`
      padding: 9px 0;
    `};
`;

const PhoneContainer = styled(InputContainer)`
  direction: ${({ theme }) => theme.direction};
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  margin: 4px 0;

  ${({ theme }) =>
    theme.newIndex &&
    css`
      margin: 9px 0;
    `};
`;

const PhoneInfo = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  width: 100%;
  margin-bottom: 8px;
  padding: 27px 20px 20px 30px;
  background: #ebebeb;
  border-radius: 4px;

  :after {
    top: 100%;
    left: 18px;
    border: solid transparent;
    content: " ";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    border-color: rgba(235, 235, 235, 0);
    border-top-color: #ebebeb;
    border-width: 8px;
    margin-left: -8px;
  }
`;

const IconContainer = styled.div`
  position: absolute;
  width: 16px;
  min-width: 16px;
  margin-right: 7px;
  top: 10px;
  left: 9px;
`;

const InfoText = styled.div`
  font-size: ${fontSize(12)};
  line-height: 14px;
  color: #343434;
  text-align: left;
`;

const CloseInfo = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 11px;
  height: 11px;
  cursor: pointer;
`;

const PhoneInputContainer = styled.div`
  @media (max-width: 767px) {
    :first-of-type {
      margin-bottom: 4px;
    }
  }

  @media (min-width: 768px) {
    flex: 1;
    flex-basis: 50%;
    max-width: 220px;
  }
`;

const EmailRestorePassword = ({ email, locale, isDarkPage, ...props }) => {
  const [pending, setPending] = useState(false);
  const [submitTimeError, setSubmitTimeError] = useState(null);
  const [responseTimeError, setResponseTimeError] = useState(null);

  async function restore(event: SyntheticEvent<HTMLButtonElement>) {
    event.preventDefault();

    const submitTimeError =
      validators.emptyFields(email) || validators.emailFull(email);

    if (submitTimeError) {
      setSubmitTimeError(submitTimeError);
      return;
    }

    setPending(true);

    const data = JSON.stringify({
      type: "email",
      data: { email },
    });

    try {
      const response = await get(`/send-restore?locale=${locale}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: data,
      });

      setPending(false);

      switch (response.status) {
        case "OK":
          props.onNotification(response.message, "restorePasswordWithEmail");
          props.onLeave(LOGIN_TAB_ID);
          break;
        case "ERROR":
          setResponseTimeError(response.message);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(error);
    }
  }

  function handleInputChange(event: SyntheticInputEvent<HTMLInputElement>) {
    setSubmitTimeError(null);
    setResponseTimeError(null);

    props.onInputChange(event);
  }

  const inputTimeError = validators.email(email);
  const errorMessage = responseTimeError || submitTimeError || inputTimeError;

  return (
    <Container>
      <form action="#" method="post" id="restoreForm">
        <div>
          <Text>
            <Gettext>
              Please enter your email. We will send you a password recovery link
              to this address.
            </Gettext>
          </Text>
        </div>
        <Input
          type="email"
          name="email"
          id="login-email2"
          required="required"
          placeholder={props.gettext`Email`}
          onChange={handleInputChange}
          value={email}
        />
        {errorMessage && <ErrorMessage message={errorMessage} />}
        <SubmitContainer>
          <BtnFormSubmit id="restore" onClick={restore} loader={pending}>
            <Gettext>Restore password</Gettext>
          </BtnFormSubmit>
        </SubmitContainer>
        {process.env.REACT_APP_DEPLOY_TARGET !== "eztrader" && (
          <SuppurLinkContainer>
            <SupportLink to="/helpdesk/contacts">
              <Gettext>Email support</Gettext>
            </SupportLink>
          </SuppurLinkContainer>
        )}
        <BackContainer isDarkPage={isDarkPage}>
          <span>
            <Gettext>Already have an account? Then</Gettext>{" "}
          </span>
          <BackToLogin onClick={() => props.onLeave(LOGIN_TAB_ID)}>
            <Gettext>login</Gettext>
          </BackToLogin>
        </BackContainer>
      </form>
    </Container>
  );
};

export function getPhoneExample(telCountry, apiCountries) {
  let phoneExample;
  if (telCountry) {
    const apiCountry = apiCountries.find(
      (country) => country.code.toUpperCase() === telCountry.code.toUpperCase()
    );
    phoneExample = apiCountry && apiCountry["phone-number"];

    const code = `+${telCountry.phoneCode}`;
    if (phoneExample && phoneExample.indexOf(code) > -1) {
      phoneExample = phoneExample.slice(code.length).trim();
    }
  }

  return phoneExample;
}

const SMSRestorePassword = ({
  tel,
  email,
  locale,
  isDarkPage,
  onSetRedirect,
  ...props
}) => {
  const [step, setStep] = useState("phone");
  const [pending, setPending] = useState(false);
  const [submitTimeError, setSubmitTimeError] = useState(null);
  const [telCountry, setTelCountry] = useState(null);
  const [showPhoneInfo, setShowPhoneInfo] = useState(false);
  const [shouldShowPhoneHint, setShouldShowPhoneHint] = useState(null);
  const [telError, setTelError] = useState(false);
  const [smsCode, setSmsCode] = useState("");
  const [codeError, setCodeError] = useState(false);

  async function submitPhoneNumber(event: SyntheticEvent<HTMLButtonElement>) {
    event.preventDefault();

    const phoneCode = telCountry && telCountry.phoneCode;

    if (phoneCode) {
      tel = ripOffLeadingZero(phoneCode, tel);
    }

    const submitTimeError = validators.emptyFields(tel) || validators.tel(tel);

    if (submitTimeError) {
      setSubmitTimeError(submitTimeError);
      return;
    }

    setPending(true);

    const data = JSON.stringify({
      type: "phone",
      data: { phone: tel },
    });

    try {
      const response = await get(`/send-restore?locale=${locale}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: data,
      });

      setPending(false);

      switch (response.status) {
        case "OK":
          setStep("code");
          break;
        case "ERROR":
          setTelError(response.payload.msg);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function submitSmsCode(event: SyntheticEvent<HTMLButtonElement>) {
    event.preventDefault();

    const submitTimeError = smsCode == null;

    if (submitTimeError) {
      setSubmitTimeError(submitTimeError);
      return;
    }

    setPending(true);

    const data = JSON.stringify({
      type: "sms-code",
      data: {
        phone: tel,
        code: smsCode,
      },
    });

    try {
      const response = await get(`/send-restore?locale=${locale}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: data,
      });

      setPending(false);

      switch (response.status) {
        case "OK":
          onSetRedirect();
          break;
        case "ERROR":
          setCodeError(response.payload.msg);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(error);
    }
  }

  function handlePhoneInputChange(
    event: SyntheticInputEvent<HTMLInputElement>
  ) {
    setTelError(null);
    setSubmitTimeError(null);

    props.onPhoneInputChange(event);
  }

  function handleSmsCodeInput(event) {
    setCodeError(null);
    setSubmitTimeError(null);
    setSmsCode(event.target.value);
  }

  function handleInputChange(event: SyntheticInputEvent<HTMLInputElement>) {
    setTelError(null);
    setSubmitTimeError(null);

    props.onInputChange(event);
  }

  const phoneExample = getPhoneExample(telCountry, props.apiCountries);

  const telErrorMessage = submitTimeError || telError;
  const codeErrorMessage = submitTimeError || codeError;

  return step === "phone" ? (
    <Container>
      <form action="#" method="post" id="restoreForm">
        <div>
          <Text>
            <Gettext>
              Please enter your phone number, and we will send you a password
              recovery code.
            </Gettext>
          </Text>
        </div>
        {FeatureFlags.useCoolPhoneInputOnAuth ? (
          <PhoneFieldContainer>
            <PhoneContainer>
              {showPhoneInfo && shouldShowPhoneHint && (
                <PhoneInfo>
                  <IconContainer>
                    <InfoIcon />
                  </IconContainer>
                  <InfoText>
                    <PhoneInputHint
                      countryCode={telCountry && telCountry.code}
                      phoneExample={phoneExample}
                    />
                  </InfoText>
                  <CloseInfo onClick={() => setShowPhoneInfo(false)}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="4454.792 3403.601 10.606 10.607"
                    >
                      <g transform="translate(3350 3251)">
                        <rect
                          width="1"
                          height="14"
                          fill="#b5b5b5"
                          transform="translate(1114.691 152.601) rotate(45)"
                        />
                        <rect
                          width="1"
                          height="14"
                          fill="#b5b5b5"
                          transform="translate(1115.398 162.5) rotate(135)"
                        />
                      </g>
                    </svg>
                  </CloseInfo>
                </PhoneInfo>
              )}
              <PhoneInput
                SelectContainer={PhoneInputContainer}
                InputContainer={PhoneInputContainer}
                defaultCountry={props.geo}
                value={tel}
                onChange={({
                  country,
                  fullNumber: phone,
                  countryAutoSwitched,
                }) => {
                  handleInputChange({
                    target: {
                      name: "tel",
                      value: phone,
                    },
                  });

                  const countryCode = telCountry && telCountry.code;
                  let shouldShowHint = countryAutoSwitched
                    ? false
                    : shouldShowPhoneHint;
                  if (!countryAutoSwitched && country.code !== countryCode) {
                    shouldShowHint = true;
                  }
                  setTelCountry(country);
                  setShouldShowPhoneHint(shouldShowHint);
                }}
                errorMessage={telErrorMessage}
                placeholder={props.gettext`Phone`}
                showInfo={<PhoneInputHint />}
                onFocus={() => setShowPhoneInfo(true)}
                onBlur={() => setShowPhoneInfo(false)}
              />
            </PhoneContainer>
          </PhoneFieldContainer>
        ) : (
          <Input
            tab="register"
            type="tel"
            name="tel"
            id="register-phone"
            required="required"
            placeholder={props.gettext`Phone`}
            value={tel}
            onChange={handlePhoneInputChange}
            autocomplete="tel"
          />
        )}
        {telErrorMessage && <ErrorMessage message={telErrorMessage} />}
        <SubmitContainer>
          <BtnFormSubmit
            id="restore"
            onClick={(e) => submitPhoneNumber(e)}
            loader={pending}
          >
            <Gettext>Restore password</Gettext>
          </BtnFormSubmit>
        </SubmitContainer>
        {process.env.REACT_APP_DEPLOY_TARGET !== "eztrader" && (
          <SuppurLinkContainer>
            <SupportLink to="/helpdesk/contacts">
              <Gettext>Email support</Gettext>
            </SupportLink>
          </SuppurLinkContainer>
        )}
        <BackContainer isDarkPage={isDarkPage}>
          <span>
            <Gettext>Already have an account? Then</Gettext>{" "}
          </span>
          <BackToLogin onClick={() => props.onLeave(LOGIN_TAB_ID)}>
            <Gettext>login</Gettext>
          </BackToLogin>
        </BackContainer>
      </form>
    </Container>
  ) : (
    <Container>
      <form action="#" method="post" id="restoreForm">
        <div>
          <Text>
            <Gettext>Please, enter the verification code!</Gettext>
          </Text>
        </div>
        <Input
          tab="sms-code"
          type="number"
          name="smscode"
          id="smscode"
          required="required"
          placeholder={props.gettext`Enter code`}
          value={smsCode}
          onChange={handleSmsCodeInput}
        />
        {codeErrorMessage && <ErrorMessage message={codeErrorMessage} />}
        <SubmitContainer>
          <BtnFormSubmit
            id="restore"
            onClick={(e) => submitSmsCode(e)}
            loader={pending}
          >
            <Gettext>Submit</Gettext>
          </BtnFormSubmit>
        </SubmitContainer>
        <BackContainer isDarkPage={isDarkPage}>
          <span>
            <Gettext>Already have an account? Then</Gettext>{" "}
          </span>
          <BackToLogin onClick={() => props.onLeave(LOGIN_TAB_ID)}>
            <Gettext>login</Gettext>
          </BackToLogin>
        </BackContainer>
      </form>
    </Container>
  );
};

const RestorePassword = (props) => {
  const [redirect, setRedirect] = useState(false);

  function onSetRedirect() {
    setRedirect(true);
  }

  if (redirect) {
    window.location.href = `${window.location.origin}/password-update`;
  }

  switch (props.restoreType) {
    case "email":
      return <EmailRestorePassword {...props} />;
    case "phone":
      return <SMSRestorePassword onSetRedirect={onSetRedirect} {...props} />;
    default:
      return null;
  }
};

export default withFetch(withGettext(RestorePassword));



// WEBPACK FOOTER //
// ./src/components/Auth/Tabs/PasswordRestore.js